import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Library, Film, Plus, Pencil, Trash2, Upload } from 'lucide-react';
import { PropertyEditor } from './PropertyEditor';
import { LibraryItem } from '../types';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { uploadMedia } from '../api/media';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface LibraryCollectionProps {
  nodes?: LibraryItem[];
  media?: LibraryItem[];
  onSelect?: (item: LibraryItem) => void;
  onAddItem?: (category: string) => void;
  onUpdateItem?: (id: string, updates: Partial<LibraryItem>) => void;
  onDeleteItem?: (id: string) => void;
  className?: string;
}

export function LibraryCollection({ 
  nodes = [], 
  media = [], 
  onSelect, 
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  className
}: LibraryCollectionProps) {
  const [editingItem, setEditingItem] = useState<LibraryItem | null>(null);
  const [deletingItem, setDeletingItem] = useState<LibraryItem | null>(null);
  const [deleteCountdown, setDeleteCountdown] = useState(3);
  const deleteCountdownRef = useRef<NodeJS.Timeout>();
  const deleteTimeoutRef = useRef<NodeJS.Timeout>();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const handleEdit = (item: LibraryItem) => {
    setEditingItem(item);
  };

  const handleSave = (updates: Partial<LibraryItem>) => {
    if (editingItem) {
      onUpdateItem?.(editingItem.id, updates);
      setEditingItem(null);
    }
  };

  const startDeleteCountdown = (item: LibraryItem) => {
    setDeletingItem(item);
    setDeleteCountdown(3);
    deleteCountdownRef.current = setInterval(() => {
      setDeleteCountdown(prev => {
        if (prev <= 1) {
          clearInterval(deleteCountdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleDelete = (e: React.MouseEvent, item: LibraryItem) => {
    e.stopPropagation();
    startDeleteCountdown(item);
  };

  const cancelDelete = () => {
    setDeletingItem(null);
    setDeleteCountdown(3);
    if (deleteCountdownRef.current) {
      clearInterval(deleteCountdownRef.current);
    }
  };

  const confirmDelete = () => {
    if (deletingItem) {
      onDeleteItem?.(deletingItem.id);
      setDeletingItem(null);
      setDeleteCountdown(3);
      if (deleteCountdownRef.current) {
        clearInterval(deleteCountdownRef.current);
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const response = await uploadMedia(file);
      if (response.error) {
        throw new Error(response.error);
      }
      console.log('Media upload response:', response);
      onUpdateItem?.(response.item.id, response.item);
    } catch (error) {
      console.error('Failed to upload media:', error);
      toast.error('Failed to upload media');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    return () => {
      if (deleteCountdownRef.current) {
        clearInterval(deleteCountdownRef.current);
      }
    };
  }, []);

  const renderGrid = (items: LibraryItem[], category: string) => {
    if (items.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          {category === 'media' ? (
            <>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileChange}
                accept="image/*,video/*,audio/*"
              />
              <Button
                variant="ghost"
                className="flex flex-col items-center gap-2 p-6"
                onClick={handleUploadClick}
                disabled={isUploading}
              >
                <Upload className="w-8 h-8" />
                <span>{isUploading ? 'Uploading...' : 'Upload Media'}</span>
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              className="flex flex-col items-center gap-2 p-6"
              onClick={() => onAddItem?.(category)}
            >
              <Plus className="w-8 h-8" />
              <span>Add {category}</span>
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-2">
        {category === 'media' && (
          <div className="relative group">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileChange}
              accept="image/*,video/*,audio/*"
            />
            <Button
              variant="ghost"
              className="flex flex-col items-center gap-2 p-2 h-auto aspect-square w-full"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              <Upload className="w-8 h-8" />
              <span>{isUploading ? 'Uploading...' : 'Upload Media'}</span>
            </Button>
          </div>
        )}
        {items.map((item) => (
          <div key={item.id} className="relative group">
            {category === 'media' ? (
              <Button
                variant="ghost"
                className="flex flex-col items-center gap-2 p-2 h-auto aspect-square w-full overflow-hidden"
                onClick={() => {
                  // Copy the URL to clipboard
                  navigator.clipboard.writeText(item.properties?.url || '');
                  toast.success('URL copied to clipboard', {
                    description: item.properties?.url
                  });
                }}
              >
                {item.type.startsWith('image/') ? (
                  <img 
                    src={item.properties?.url} 
                    alt={item.name}
                    className="w-full h-full object-cover rounded-md"
                  />
                ) : item.type.startsWith('video/') ? (
                  <video 
                    src={item.properties?.url}
                    className="w-full h-full object-cover rounded-md"
                    controls
                  />
                ) : (
                  <Film className="w-8 h-8" />
                )}
                <div className="text-xs text-muted-foreground absolute bottom-1 left-1 right-1 text-center bg-background/80 rounded p-1">
                  {item.name}
                </div>
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="flex flex-col items-center gap-2 p-4 h-auto aspect-square w-full"
                onClick={() => onSelect?.(item)}
              >
                <div className="text-sm font-medium">{item.name}</div>
                <div className="text-xs text-muted-foreground">{item.type}</div>
              </Button>
            )}
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {category !== 'media' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => handleDelete(e, item)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={cn("flex", className)}>
      <div className="flex gap-1">
        <Button
          variant={activeTab === 'nodes' ? 'default' : 'ghost'}
          size="sm"
          className="flex items-center gap-2 relative"
          onClick={() => setActiveTab(activeTab === 'nodes' ? null : 'nodes')}
        >
          <Library className="w-4 h-4" />
          <span>Nodes</span>
          {activeTab === 'nodes' && (
            <motion.div 
              className="absolute inset-0 bg-primary/10 rounded-md"
              layoutId="active-tab"
              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
            />
          )}
        </Button>
        <Button
          variant={activeTab === 'media' ? 'default' : 'ghost'}
          size="sm"
          className="flex items-center gap-2 relative"
          onClick={() => setActiveTab(activeTab === 'media' ? null : 'media')}
        >
          <Film className="w-4 h-4" />
          <span>Media</span>
          {activeTab === 'media' && (
            <motion.div 
              className="absolute inset-0 bg-primary/10 rounded-md"
              layoutId="active-tab"
              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
            />
          )}
        </Button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab && (
          <motion.div 
            className="absolute top-14 right-4 w-64 bg-background border rounded-lg shadow-lg overflow-hidden"
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 300, y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            transition={{ 
              type: "spring",
              bounce: 0.2,
              duration: 0.6,
              opacity: { duration: 0.2 }
            }}
          >
            <ScrollArea className="h-[300px]">
              <motion.div 
                className="p-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                {activeTab === 'nodes' && renderGrid(nodes, 'nodes')}
                {activeTab === 'media' && renderGrid(media, 'media')}
              </motion.div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {editingItem && (
        <Dialog open={true} onOpenChange={() => setEditingItem(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit {editingItem.category.slice(0, -1)}</DialogTitle>
            </DialogHeader>
            <PropertyEditor
              node={editingItem}
              onUpdate={handleSave}
              onClose={() => setEditingItem(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {deletingItem && (
        <AlertDialog open={true} onOpenChange={cancelDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {deletingItem.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. Deleting in {deleteCountdown}...
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
